import { useEffect, useId, useRef } from 'react';
import type { GalleryImage } from '../../types/gallery';
import { Badge } from '../atoms/Badge/Badge';
import { Button } from '../atoms/Button/Button';
import { Checkbox } from '../atoms/Checkbox';
import { LiveRegion } from '../atoms/LiveRegion/LiveRegion';
import { Text } from '../atoms/Typography/Text';
import { useTranslation } from '../../i18n/useTranslation';
import { galleryImageDate, galleryImageCaption, formatGalleryDate } from './galleryImageMeta';
import './GalleryGrid.css';

export interface GalleryGridProps {
  images: GalleryImage[];
  isLoading: boolean;
  hasNextPage: boolean;
  onLoadMore: () => void;
  onImageClick: (index: number) => void;
  startIndex?: number;
  /** Selection mode ("Select"): tiles become labelled checkboxes instead of opening the viewer. */
  selectionMode?: boolean;
  selectedIds?: ReadonlySet<string>;
  onToggleSelect?: (imageId: string) => void;
}

/**
 * The square photo area of a tile: the whole photo (object-fit: contain) on a black
 * letterbox, so every tile has the same size whatever the photo's aspect ratio.
 * Uses the ~400 px WebP thumbnail; falls back to the display variant / original.
 */
function TileMedia({ image, alt }: { image: GalleryImage; alt: string }) {
  return (
    <span className="gallery-grid__media">
      <img
        src={image.thumbnailUrl ?? image.displayUrl ?? undefined}
        alt={alt}
        className="gallery-grid__image"
        loading="lazy"
        decoding="async"
        draggable={false}
      />
    </span>
  );
}

interface TileProps {
  image: GalleryImage;
  index: number;
  onImageClick: (index: number) => void;
}

/** A photo tile: one real button (keyboard operable) that opens the detail view. */
function GalleryTile({ image, index, onImageClick }: TileProps) {
  const { t, i18n } = useTranslation();
  const captionId = useId();
  const iso = galleryImageDate(image);
  const date = formatGalleryDate(iso, i18n.language);
  const caption = galleryImageCaption(image);

  return (
    <Button
      variant="ghost"
      className="gallery-grid__tile"
      aria-label={
        image.hasAiAnalysis === true
          ? t('gallery.grid.viewImageAi', { date })
          : t('gallery.grid.viewImage', { date })
      }
      aria-describedby={caption ? captionId : undefined}
      onClick={() => onImageClick(index)}
      data-testid="gallery-tile"
    >
      <TileMedia image={image} alt={image.originalFilename || t('gallery.grid.imageAlt')} />
      <span className="gallery-grid__meta">
        <time dateTime={iso} className="gallery-grid__date">
          {date}
        </time>
        {image.hasAiAnalysis === true && (
          <Badge tone="ai" className="gallery-grid__ai-badge">
            {t('gallery.aiBadge')}
          </Badge>
        )}
      </span>
      {/* Always one caption line (empty without a note) so every tile has the same height. */}
      <span id={caption ? captionId : undefined} className="gallery-grid__caption" aria-hidden={caption ? undefined : true}>
        {caption ?? '\u00a0'}
      </span>
    </Button>
  );
}

function SkeletonTile() {
  return (
    <div className="gallery-grid__card gallery-grid__card--skeleton" data-testid="gallery-skeleton">
      <span className="gallery-grid__media gallery-grid__media--skeleton" />
      <span className="gallery-grid__meta">
        <span className="gallery-grid__skeleton-line" />
      </span>
    </div>
  );
}

interface SelectableTileProps {
  image: GalleryImage;
  selected: boolean;
  onToggle: (imageId: string) => void;
}

/**
 * A tile in selection mode: the same card, with a checkbox whose label covers the
 * whole tile (one click/tab target). Selection shows as the checked box (not colour
 * only) plus a thicker outline.
 */
function SelectableTile({ image, selected, onToggle }: SelectableTileProps) {
  const { t, i18n } = useTranslation();
  const iso = galleryImageDate(image);
  const date = formatGalleryDate(iso, i18n.language);
  return (
    <div
      className={`gallery-grid__card gallery-grid__card--selectable${selected ? ' gallery-grid__card--selected' : ''}`}
      data-testid="gallery-select-tile"
    >
      <TileMedia image={image} alt="" />
      <span className="gallery-grid__meta">
        <Checkbox
          className="gallery-grid__select"
          label={t('gallery.select.imageLabel', { name: image.originalFilename || t('gallery.grid.imageAlt'), date })}
          checked={selected}
          onChange={() => onToggle(image.id)}
        />
      </span>
    </div>
  );
}

export function GalleryGrid({
  images,
  isLoading,
  hasNextPage,
  onLoadMore,
  onImageClick,
  startIndex = 0,
  selectionMode = false,
  selectedIds,
  onToggleSelect,
}: GalleryGridProps) {
  const { t } = useTranslation();
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (sentinel == null) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first != null && first.isIntersecting && hasNextPage && !isLoading) {
          onLoadMore();
        }
      },
      { rootMargin: '200px' },
    );

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
    };
  }, [hasNextPage, isLoading, onLoadMore]);

  const skeletonCount = isLoading && images.length === 0 ? 12 : isLoading ? 4 : 0;

  return (
    <div className="gallery-grid" data-testid="gallery-grid">
      <ul className="gallery-grid__list" aria-label={t('gallery.grid.label')} aria-busy={isLoading || undefined}>
        {images.map((image, index) => (
          <li key={image.id} className="gallery-grid__item">
            {selectionMode && onToggleSelect ? (
              <SelectableTile image={image} selected={selectedIds?.has(image.id) ?? false} onToggle={onToggleSelect} />
            ) : (
              <GalleryTile image={image} index={startIndex + index} onImageClick={onImageClick} />
            )}
          </li>
        ))}

        {Array.from({ length: skeletonCount }, (_, i) => (
          <li key={`skeleton-${i}`} className="gallery-grid__item" aria-hidden="true">
            <SkeletonTile />
          </li>
        ))}
      </ul>

      {images.length === 0 && !isLoading && (
        <LiveRegion className="gallery-grid__empty-state">
          <Text>{t('gallery.grid.empty')}</Text>
        </LiveRegion>
      )}

      {hasNextPage && (
        <div ref={sentinelRef} className="gallery-grid__sentinel" aria-hidden="true" data-testid="load-more-sentinel" />
      )}
    </div>
  );
}
